import { useEffect, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { api, errMsg, BACKEND } from "@/lib/api";
import { Pill } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TONE = { created: "green", updated: "blue", skipped: "slate", error: "red" };

export function CsvImport({ open, onOpenChange, onDone }) {
  const [csv, setCsv] = useState("");
  const [file, setFile] = useState("");
  const [mode, setMode] = useState("upsert");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [servers, setServers] = useState([]);
  const [grants, setGrants] = useState([]);
  useEffect(() => { if (open) api.get("/admin/servers").then(({ data }) => setServers(data)).catch(() => {}); }, [open]);
  const toggle = (id) => setGrants((g) => (g.includes(id) ? g.filter((x) => x !== id) : [...g, id]));

  const run = async (dry, text = csv, m = mode) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/channels-csv/import", { csv: text, mode: m, dry_run: dry, grant_server_ids: grants });
      setRes(data);
      if (!dry) {
        const g = (data.grants || []).reduce((a, x) => a + x.new_grants, 0);
        toast.success(`Imported: ${data.summary.created} created, ${data.summary.updated} updated${grants.length ? ` · ${g} new grant(s) on ${grants.length} server(s)` : ""}`); onDone?.();
      }
    } catch (e) { toast.error(errMsg(e)); setRes(null); } finally { setBusy(false); }
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const text = await f.text();
    setCsv(text); setFile(f.name); run(true, text);
  };
  const close = (v) => { if (!v) { setCsv(""); setFile(""); setRes(null); setGrants([]); } onOpenChange(v); };
  const s = res?.summary;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="csv-import-dialog" className="max-w-3xl bg-[#0F1722] border-slate-700 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Import channels from CSV</DialogTitle>
          <DialogDescription className="text-xs text-slate-400">
            Columns: slug, name, type, audio_policy, failback_window, enabled, source1_url, source1_label … source5_url, source5_label. Only slug and source1_url are required.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center h-9 px-3 rounded border border-slate-700 text-sm text-slate-200 cursor-pointer hover:bg-slate-800">
            {file || "Choose CSV file…"}
            <input data-testid="csv-file-input" type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} />
          </label>
          {csv && <Button data-testid="csv-preview-btn" size="sm" variant="ghost" onClick={() => run(true)}>Refresh preview</Button>}
          <Select value={mode} onValueChange={(v) => { setMode(v); if (csv) run(true, csv, v); }}>
            <SelectTrigger data-testid="csv-mode-select" className="w-64 h-9 bg-[#0B111B] border-slate-700 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#16202E] border-slate-700">
              <SelectItem value="upsert">Create new + update existing (by slug)</SelectItem>
              <SelectItem value="create_only">Create new only (skip existing)</SelectItem>
            </SelectContent>
          </Select>
          <a data-testid="csv-template-btn" href={`${BACKEND}/api/admin/channels-csv/export?template=true`} className="text-xs text-cyan-400 hover:underline">Download template</a>
        </div>
        <div data-testid="csv-grants" className="border border-slate-800 rounded p-3 bg-[#0B111B]">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Also grant imported channels to</div>
          {servers.length === 0 && <p className="text-xs text-slate-500">No authorized servers yet.</p>}
          <div className="grid grid-cols-2 gap-1">
            {servers.map((sv) => (
              <label key={sv.id} className="flex items-center gap-2 text-sm text-slate-300">
                <Checkbox data-testid={`csv-grant-${sv.id}`} checked={grants.includes(sv.id)} disabled={sv.all_channels} onCheckedChange={() => toggle(sv.id)} />
                {sv.name} <span className="font-mono text-[11px] text-slate-500">{sv.all_channels ? "already all channels" : `${sv.channels.length} granted`}</span>
              </label>
            ))}
          </div>
          {res?.grants?.length > 0 && <div className="mt-2 flex flex-wrap gap-1">{res.grants.map((g) => <Pill key={g.server_id} tone="cyan" testid={`csv-grant-result-${g.server_id}`}>{g.server}: {g.all_channels ? "already covers all channels" : `+${g.new_grants} grant(s)`}{res.dry_run ? " (preview)" : ""}</Pill>)}</div>}
        </div>
        {res && (
          <div className="mt-2">
            <div className="flex gap-2 mb-2" data-testid="csv-summary">
              {res.dry_run && <Pill tone="amber">preview — nothing saved yet</Pill>}
              {Object.entries(s).map(([k, v]) => <Pill key={k} tone={TONE[k]}>{v} {k}</Pill>)}
            </div>
            <div className="max-h-72 overflow-y-auto border border-slate-800 rounded">
              <table className="w-full text-xs font-mono">
                <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-500"><th className="p-2">Row</th><th>Slug</th><th>Result</th><th>Details</th></tr></thead>
                <tbody>{res.results.map((r) => (
                  <tr key={r.row} data-testid={`csv-result-row-${r.row}`} className="border-t border-slate-800/60">
                    <td className="p-2 text-slate-500">{r.row}</td><td className="text-slate-200">{r.slug || "—"}</td>
                    <td><Pill tone={TONE[r.action]}>{r.action}</Pill></td>
                    <td className="text-slate-400">{r.error || (r.sources != null ? `${r.sources} source(s)` : "")}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => close(false)}>Close</Button>
          <Button data-testid="csv-apply-btn" disabled={busy || !res || !res.dry_run || !(s.created + s.updated)} onClick={() => run(false)} className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">
            {busy ? "Working…" : `Import ${s ? s.created + s.updated : 0} channel(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
